import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterCaseStatus, SinisterNegotiationService } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { CASE_STATUS_OPEN_LABEL, CASE_STATUS_OPTIONS, getCaseStatusChipColors, getCaseStatusLabel } from "../../constants/caseStatusOptions";
import { FollowUpGenerationOptions } from "../FollowUpGenerationOptions";
import { generateFollowUps } from "../../utils/generateFollowUps";

interface Props {
	open: boolean;
	sinisterId: string;
	currentStatus?: SinisterCaseStatus;
	// Prazo (deadline) já registrado para esta cobrança, se houver — ver PR-07-cobranca-deadline-inicio-atuacao-frontend.md.
	deadlineDate?: string | null;
	onClose: () => void;
	onUpdated?: () => void;
}

const COLLECTION_STARTED_STATUS: SinisterCaseStatus = "collection_started";

export const ModalUpdateCaseStatus = ({ open, sinisterId, currentStatus, deadlineDate, onClose, onUpdated }: Props) => {
	const toastHandler = useToast();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formStatus, setFormStatus] = useState<SinisterCaseStatus>(currentStatus ?? "collection_started");
	const [formNote, setFormNote] = useState("");
	const [formFile, setFormFile] = useState<File | null>(null);
	const [generateFollowUp, setGenerateFollowUp] = useState(true);
	// Follow up externo é visível pro sinistro e pra imobiliária (e dispara e-mail aos contatos
	// cadastrados) — por isso começa desmarcado, é uma escolha explícita de cada lançamento.
	const [generateExternalFollowUp, setGenerateExternalFollowUp] = useState(false);
	const [showRestartDeadlineConfirm, setShowRestartDeadlineConfirm] = useState(false);

	useEffect(() => {
		if (open) {
			setFormStatus(currentStatus ?? "collection_started");
			setFormNote("");
			setFormFile(null);
			setGenerateFollowUp(true);
			setGenerateExternalFollowUp(false);
			setShowRestartDeadlineConfirm(false);
		}
	}, [open, currentStatus]);

	const currentColors = getCaseStatusChipColors(currentStatus);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			setFormFile(event.target.files[0]);
		}
	};

	const handleSubmit = async () => {
		if (!formNote.trim()) {
			toastHandler.present({ type: "error", messageOrContent: "Observação é obrigatória.", position: "top-right" });
			return;
		}

		// Reentrar em "Início da Atuação de Cobrança" com um prazo já registrado reinicia a contagem —
		// confirma com o usuário antes de sobrescrever, já que ele pode não ter percebido que já existe um prazo em curso.
		if (formStatus === COLLECTION_STARTED_STATUS && deadlineDate && !showRestartDeadlineConfirm) {
			setShowRestartDeadlineConfirm(true);
			return;
		}

		await submitStatusUpdate();
	};

	const submitStatusUpdate = async () => {
		setIsSubmitting(true);
		const result = await trackAsync(
			"negotiation.update_case_status",
			() =>
				SinisterNegotiationService.updateCaseStatus(sinisterId, {
					status: formStatus,
					note: formNote,
					file: formFile ?? undefined,
				}),
			{ sinisterId }
		);

		if (result instanceof Error) {
			setIsSubmitting(false);
			setShowRestartDeadlineConfirm(false);
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		await generateFollowUps({
			sinisterId,
			note: `<p>Status atualizado para: ${getCaseStatusLabel(formStatus)}</p><p>${formNote}</p>`,
			generateInternal: generateFollowUp,
			generateExternal: generateExternalFollowUp,
			trackLabel: "update_case_status",
		});

		setIsSubmitting(false);
		setShowRestartDeadlineConfirm(false);
		toastHandler.present({ type: "success", messageOrContent: "Status da cobrança atualizado com sucesso!", position: "top-right" });
		onUpdated?.();
		onClose();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Alteração de Status da Cobrança
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2.5} mt={0.5}>
						<Box>
							<Typography variant="body2" color="text.secondary" mb={0.5}>
								Status atual
							</Typography>
							<Chip
								label={currentStatus ? getCaseStatusLabel(currentStatus) : CASE_STATUS_OPEN_LABEL}
								sx={{ fontWeight: 500, bgcolor: currentColors.bg, color: currentColors.color, border: "0.5px solid #f1f1f1" }}
							/>
						</Box>

						<Divider />

						<TextField
							select
							label="Novo status"
							value={formStatus}
							onChange={(e) => setFormStatus(e.target.value as SinisterCaseStatus)}
							fullWidth
							size="small"
						>
							{CASE_STATUS_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						<FollowUpGenerationOptions
							generateInternal={generateFollowUp}
							onChangeInternal={setGenerateFollowUp}
							generateExternal={generateExternalFollowUp}
							onChangeExternal={setGenerateExternalFollowUp}
						/>

						{formStatus === COLLECTION_STARTED_STATUS && deadlineDate && (
							<Alert severity="warning">
								Esta cobrança já possui um prazo em andamento, com vencimento em{" "}
								<strong>{format(parseISO(deadlineDate), "dd/MM/yyyy")}</strong>. Ao registrar este status novamente, o prazo será
								reiniciado (novos 20 dias a partir de hoje).
							</Alert>
						)}

						<TextField
							label="Observação"
							required
							multiline
							minRows={3}
							size="small"
							value={formNote}
							onChange={(e) => setFormNote(e.target.value)}
							fullWidth
						/>

						<Box>
							{formFile ? (
								<Paper variant="outlined" sx={{ px: 2, py: 1 }}>
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Stack direction="row" spacing={1} alignItems="center">
											<InsertDriveFileIcon fontSize="small" sx={{ color: "#999" }} />
											<Typography variant="body2" fontWeight={500}>
												{formFile.name}
											</Typography>
										</Stack>
										<IconButton size="small" color="error" onClick={() => setFormFile(null)}>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Stack>
								</Paper>
							) : (
								<Box
									sx={{ border: "2px dashed #FFD700", borderRadius: 2, p: 3, cursor: "pointer", textAlign: "center" }}
									onClick={() => document.getElementById("update-case-status-attachment-input")?.click()}
								>
									<UploadIcon sx={{ fontSize: 36, color: "#666", mb: 0.5 }} />
									<Typography variant="body2" color="text.secondary">
										Clique para anexar um arquivo (opcional)
									</Typography>
								</Box>
							)}
							<input id="update-case-status-attachment-input" type="file" hidden onChange={handleFileChange} />
						</Box>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Button variant="contained" disabled={isSubmitting} onClick={handleSubmit}>
						Registrar Status
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={showRestartDeadlineConfirm} onClose={() => setShowRestartDeadlineConfirm(false)} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontWeight: 400 }}>Reiniciar prazo da cobrança?</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2">
						Esta cobrança já possui um prazo em andamento, com vencimento em{" "}
						<strong>{deadlineDate ? format(parseISO(deadlineDate), "dd/MM/yyyy") : ""}</strong>. Ao confirmar, um novo prazo de 20 dias
						será iniciado a partir de hoje, substituindo o prazo atual.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowRestartDeadlineConfirm(false)}>Cancelar</Button>
					<Button variant="contained" color="warning" disabled={isSubmitting} onClick={handleSubmit}>
						Confirmar e reiniciar prazo
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
